/**
 * PlotlyChart.tsx — Chart wrapper with accessibility, error boundary, and table toggle.
 * v3.53 - 2026-02-24 - Story 13: Import shared utils from chartUtils.ts (ADR-002)
 * v3.50 - 2026-02-24 - Story 9F: useResizeHandler + fluid height for responsive charts (ADR-002)
 * v3.47 - 2026-02-24 - Story 9C: onPlotClick prop for bar click tooltip (ADR-002)
 * v3.38 - 2026-02-23 - Story 8: Error logging with report ID (ADR-002)
 * v3.37 - 2026-02-23 - Story 7B: Visual fixes (Issues 3,7,8) (ADR-002)
 * v3.35 - 2026-02-23 - Story 6: PlotlyChart.tsx (ADR-002)
 *
 * Renders a single chart from pre-computed visualization metadata.
 * Uses chartMapper.ts for all Plotly trace/layout translation.
 */
import React, { Component, useState, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Plot } from '../utils/plotlySetup';
import { mapReportToPlotly } from '../utils/chartMapper';
import { formatTitle, formatColumnLabel, calculateChartHeight, isBarType } from '../utils/chartUtils'; // v3.53 - Story 13: shared utils
import type { ChartReportData, VisualizationMetadata } from '../services/api';

// ============================================================================
// DATA TABLE — used for useTable, toggle, and screen reader fallback
// ============================================================================

const ROWS_PER_PAGE = 25;

interface DataTableProps {
  report: ChartReportData;
  columnMap?: Record<string, string>;
}

function DataTable({ report, columnMap }: DataTableProps) {
  const [page, setPage] = useState(0);
  const rows = report.data;
  const cols = report.columns;
  const paged = rows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  return (
    <TableContainer sx={{ maxHeight: 400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {cols.map((c) => (
              <TableCell key={c.name} align={c.type === 'number' ? 'right' : 'left'} sx={{ fontWeight: 600 }}>
                {columnMap?.[c.name] ?? formatColumnLabel(c.name)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paged.map((row, i) => (
            <TableRow key={i}>
              {cols.map((c) => (
                <TableCell key={c.name} align={c.type === 'number' ? 'right' : 'left'}>
                  {c.type === 'number' && typeof row[c.name] === 'number'
                    ? row[c.name].toLocaleString()
                    : row[c.name] ?? '\u2014'}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > ROWS_PER_PAGE && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          onPageChange={(_, p) => setPage(p)}
        />
      )}
    </TableContainer>
  );
}

// ============================================================================
// ERROR BOUNDARY
// ============================================================================

interface EBProps { title: string; report: ChartReportData; children: React.ReactNode }
interface EBState { hasError: boolean }

class ChartErrorBoundary extends Component<EBProps, EBState> {
  state: EBState = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  // v3.38 - Log error with report ID for debugging
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[PlotlyChart] Render crash for report "${this.props.title}":`, error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box p={2}>
          <Typography variant="h6">{this.props.title}</Typography>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            Unable to render chart. View as table instead.
          </Typography>
          <DataTable report={this.props.report} />
        </Box>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface PlotlyChartProps {
  report: ChartReportData;
  viz: VisualizationMetadata | null | undefined;
  height?: number;
  showExport?: boolean;
  loading?: boolean;
  onPlotClick?: (event: any) => void; // v3.47 - Story 9C: bar click tooltip callback
  skipSort?: boolean; // v3.47 - Story 9B fix: let caller control sort order
}

export default function PlotlyChart({ report, viz, showExport = true, loading = false, onPlotClick, skipSort }: PlotlyChartProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [showDataTable, setShowDataTable] = useState(false);

  const result = useMemo(() => mapReportToPlotly(report, viz, mode, skipSort ? { skipSort: true } : undefined), [report, viz, mode, skipSort]);
  const title = viz?.labels?.title ?? formatTitle(report.title);
  const altText = viz?.accessibility?.altText ?? `Chart: ${report.title}`;

  // v3.37 - Issue 7: Dynamic height (v3.53 - via shared calculateChartHeight)
  const chartType = viz?.chartType ?? 'horizontal_bar';
  const nonZeroCount = (isBarType(chartType) || !viz?.chartType)
    ? report.data.filter((row) => Object.values(row).some((v) => typeof v === 'number' && v !== 0)).length
    : report.data.length;
  const chartHeight = calculateChartHeight('inline', chartType, nonZeroCount);

  // Loading state
  if (loading) {
    return (
      <Paper elevation={1} sx={{ height: '100%', p: 2 }}>
        <Skeleton variant="text" width="60%" height={28} />
        <Skeleton variant="rectangular" height={chartHeight} sx={{ mt: 1, borderRadius: 1 }} />
      </Paper>
    );
  }

  // Empty state
  if (viz?.isEmpty || report.data.length === 0) {
    return (
      <Paper elevation={1} sx={{ height: '100%' }}>
        <Box p={2}><Typography variant="h6">{title}</Typography></Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight }}>
          <Typography color="text.secondary">No data available for this period</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ height: '100%' }}>
      <Box p={2} pb={0}>
        <Typography variant="h6" sx={{ fontSize: '1rem' }}>{title}</Typography>
        {viz?.labels?.subtitle && (
          <Typography variant="body2" color="text.secondary">{viz.labels.subtitle}</Typography>
        )}
      </Box>

      <ChartErrorBoundary title={title} report={report}>
        {/* Accessibility: screen readers get the insight description */}
        <div role="img" aria-label={altText}>
          {result.useTable ? (
            <Box px={2} pb={2}>
              <DataTable report={report} columnMap={viz?.labels?.columnMap} />
            </Box>
          ) : (
            <Plot
              data={result.data}
              layout={{ ...result.layout, height: chartHeight }}
              config={{
                responsive: true,
                displaylogo: false,
                displayModeBar: 'hover', // v3.37 - Issue 3: hover-only, not always visible
                modeBarButtonsToRemove: ['lasso2d', 'select2d'],
              }}
              useResizeHandler={true} // v3.50 - Story 9F: redraw on container resize
              style={{ width: '100%', height: '100%' }} // v3.50 - fluid height fills container
              onClick={onPlotClick} // v3.47 - Story 9C: bar click tooltip
            />
          )}
        </div>
      </ChartErrorBoundary>

      {/* v3.37 - Issue 8: Styled toggle button at bottom-right */}
      {!result.useTable && (
        <Box px={2} pb={1.5} display="flex" justifyContent="flex-end">
          <Button
            size="small"
            variant="text"
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              textTransform: 'none',
              '&:hover': { color: 'primary.main' },
            }}
            onClick={() => setShowDataTable((v) => !v)}
          >
            {showDataTable ? '\u2190 Back to chart' : 'View as table \u2192'}
          </Button>
        </Box>
      )}
      {!result.useTable && showDataTable && (
        <Box px={2} pb={2}>
          <DataTable report={report} columnMap={viz?.labels?.columnMap} />
        </Box>
      )}

      {/* Visually hidden table for screen readers (WCAG 1.1.1) */}
      <Box
        component="table"
        sx={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
        aria-hidden="false"
      >
        <caption>{altText}</caption>
        <thead>
          <tr>{report.columns.map((c) => <th key={c.name}>{formatColumnLabel(c.name)}</th>)}</tr>
        </thead>
        <tbody>
          {report.data.slice(0, 50).map((row, i) => (
            <tr key={i}>{report.columns.map((c) => <td key={c.name}>{row[c.name]}</td>)}</tr>
          ))}
        </tbody>
      </Box>
    </Paper>
  );
}
